import React from 'react';
import { Nullable } from 'common/types/nullable';
import getScopedTranslateMethod from 'common/js_utils/getScopedTranslateMethod';

import { AsyncValueState } from 'common/providers/AsyncValues/types';

const translate = getScopedTranslateMethod('common.providers');

/** Utility class for AsyncValue operations and helpers. */
export default class AsyncValueUtils {
  /** Safely updates state, handling test environments and React concurrent features. */
  private static safeSetState<T>(
    isMountedRef: React.MutableRefObject<boolean>,
    setState: React.Dispatch<React.SetStateAction<AsyncValueState<T>>>,
    update: (prev: AsyncValueState<T>) => AsyncValueState<T>
  ): void {
    if (!isMountedRef.current) return;

    // Additional check for test environment document availability
    if (typeof document === 'undefined' || !document) {
      return;
    }

    // Use startTransition if available (React 18+) to mark as non-urgent update
    const ReactWithTransition = React as any;
    if (typeof ReactWithTransition.startTransition === 'function') {
      ReactWithTransition.startTransition(() => {
        if (isMountedRef.current && typeof document !== 'undefined' && document) {
          setState(update);
        }
      });
    } else {
      if (isMountedRef.current && typeof document !== 'undefined' && document) {
        setState(update);
      }
    }
  }

  /** Executes fetch function with safe state management and in-flight request tracking. */
  static async fetchState<T>(
    isMountedRef: React.MutableRefObject<boolean>,
    setState: React.Dispatch<React.SetStateAction<AsyncValueState<T>>>,
    fetchFunction: () => Promise<T>,
    inFlightRequestRef: React.MutableRefObject<Promise<Nullable<T>> | null>
  ): Promise<Nullable<T>> {
    // Early exit if component is already unmounted
    if (!isMountedRef.current) {
      return null;
    }

    // Set loading state only if mounted
    AsyncValueUtils.safeSetState(isMountedRef, setState, (prev) => ({ ...prev, loading: true, error: null }));

    // Create cancellation-aware promise
    const promise = new Promise<Nullable<T>>((resolve) => {
      fetchFunction()
        .then((data) => {
          if (isMountedRef.current) {
            resolve(data);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          if (isMountedRef.current) {
            const errorMessage = AsyncValueUtils.formatError(error);
            AsyncValueUtils.safeSetState(isMountedRef, setState, (prev) => ({
              ...prev,
              loading: false,
              error: errorMessage
            }));
          }
          resolve(null);
        });
    }).finally(() => {
      // Clean up loading state and request tracking only if still mounted and document exists
      if (isMountedRef.current && typeof document !== 'undefined' && document) {
        AsyncValueUtils.safeSetState(isMountedRef, setState, (prev) => ({ ...prev, loading: false }));
      }
      inFlightRequestRef.current = null;
    });

    inFlightRequestRef.current = promise;

    const data = await promise;
    if (data !== null && isMountedRef.current && typeof document !== 'undefined' && document) {
      AsyncValueUtils.safeSetState(isMountedRef, setState, (prev) => ({
        ...prev,
        value: data,
        loading: false,
        error: null
      }));
    }
    return data;
  }

  /** Format error message for user display. */
  static formatError(error: unknown): string {
    return translate('errors.data_fetch_failure', {
      message: error instanceof Error ? error.message : String(error)
    });
  }

  /** Generate cache key from object properties. */
  static generateCacheKey(obj: Record<string, unknown>): string {
    return Object.keys(obj)
      .sort()
      .map((key) => `${key}:${String(obj[key])}`)
      .join('|');
  }

  /** Generate cache key from URL parameters. */
  static generateUrlCacheKey(baseUrl: string, params?: Record<string, string>): string {
    return !params || Object.keys(params).length === 0
      ? baseUrl
      : `${baseUrl}?${new URLSearchParams(params).toString()}`;
  }

  /** Generate timestamp-based cache key for time-sensitive data. */
  static generateTimestampCacheKey(baseKey: string, ttlMs: number = 300000): string {
    return `${baseKey}:${Math.floor(Date.now() / ttlMs)}`;
  }

  /** Create initial async value state. */
  static createInitialState<T>(initialValue: Nullable<T> = null): AsyncValueState<T> {
    return { value: initialValue, loading: false, error: null };
  }
}
